/**
 * プリセット管理用の API hooks
 */
import { useCallback, useEffect, useState } from "react";
import { RestApiError, restClient } from "../lib/rest-client";
import type { MatchingResultItem } from "./use-free-matching-api";

/**
 * プリセットの型
 */
export interface Preset {
  id: string;
  name: string;
  presetType: string;
  sourceText: string | null;
  skills: string[] | null;
  reward: number | null;
  station: string | null;
  isRemote: boolean | null;
  remoteFrequency: string | null;
  availableFrom: string | null;
  industryCategory: string | null;
  note: string | null;
  age: number | null;
  hrTypes: string | null;
  isJapanese: boolean | null;
  freelanceEnabled: boolean | null;
  ageLimitation: number | null;
  otherCountriesEnabled: boolean | null;
  isActive: boolean;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * プリセット一覧レスポンスの型
 */
interface PresetListResponse {
  items: Preset[];
  total: number;
  page: number;
  limit: number;
}

/**
 * プリセット作成リクエストの型
 */
export interface CreatePresetInput {
  name: string;
  presetType: string;
  sourceText?: string | null;
  skills?: string[] | null;
  reward?: number | null;
  station?: string | null;
  isRemote?: boolean | null;
  remoteFrequency?: string | null;
  availableFrom?: string | null;
  industryCategory?: string | null;
  note?: string | null;
  age?: number | null;
  hrTypes?: string | null;
  isJapanese?: boolean | null;
  freelanceEnabled?: boolean | null;
  ageLimitation?: number | null;
  otherCountriesEnabled?: boolean | null;
}

/**
 * プリセット更新リクエストの型
 */
export interface UpdatePresetInput {
  name: string;
  sourceText?: string | null;
  skills?: string[] | null;
  reward?: number | null;
  station?: string | null;
  isRemote?: boolean | null;
  remoteFrequency?: string | null;
  availableFrom?: string | null;
  industryCategory?: string | null;
  note?: string | null;
  age?: number | null;
  hrTypes?: string | null;
  isJapanese?: boolean | null;
  freelanceEnabled?: boolean | null;
  ageLimitation?: number | null;
  otherCountriesEnabled?: boolean | null;
}

/**
 * AI テキスト解析レスポンスの型
 */
export interface ParseTextResult {
  name: string | null;
  presetType: string | null;
  skills: string[] | null;
  reward: number | null;
  station: string | null;
  isRemote: boolean | null;
  remoteFrequency: string | null;
  availableFrom: string | null;
  industryCategory: string | null;
  age: number | null;
  hrTypes: string | null;
  isJapanese: boolean | null;
  freelanceEnabled: boolean | null;
  ageLimitation: number | null;
  otherCountriesEnabled: boolean | null;
}

/**
 * プリセット一覧を取得する hook
 * @return {object} presets, total, loading, error, refetch
 */
export function usePresets(): {
  presets: Preset[];
  total: number;
  loading: boolean;
  error: RestApiError | null;
  refetch: () => Promise<void>;
} {
  const [presets, setPresets] = useState<Preset[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<RestApiError | null>(null);

  const fetchPresets = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response =
        await restClient.get<PresetListResponse>("/matching/presets");
      setPresets(response.items);
      setTotal(response.total);
    } catch (e) {
      if (e instanceof RestApiError) {
        setError(e);
      } else {
        setError(
          new RestApiError(
            e instanceof Error ? e.message : "Unknown error",
            500,
          ),
        );
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPresets();
  }, [fetchPresets]);

  return { presets, total, loading, error, refetch: fetchPresets };
}

/**
 * プリセット詳細を取得する hook
 * @param {string | undefined} presetId プリセットID
 * @return {object} preset, loading, error, refetch
 */
export function usePreset(presetId: string | undefined): {
  preset: Preset | null;
  loading: boolean;
  error: RestApiError | null;
  refetch: () => Promise<void>;
} {
  const [preset, setPreset] = useState<Preset | null>(null);
  const [loading, setLoading] = useState(!!presetId);
  const [error, setError] = useState<RestApiError | null>(null);

  const fetchPreset = useCallback(async () => {
    if (!presetId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await restClient.get<Preset>(
        `/matching/presets/${presetId}`,
      );
      setPreset(response);
    } catch (e) {
      if (e instanceof RestApiError) {
        setError(e);
      } else {
        setError(
          new RestApiError(
            e instanceof Error ? e.message : "Unknown error",
            500,
          ),
        );
      }
    } finally {
      setLoading(false);
    }
  }, [presetId]);

  useEffect(() => {
    fetchPreset();
  }, [fetchPreset]);

  return { preset, loading, error, refetch: fetchPreset };
}

/**
 * プリセットを作成する hook
 * @return {object} createPreset, loading, error
 */
export function useCreatePreset(): {
  createPreset: (input: CreatePresetInput) => Promise<Preset>;
  loading: boolean;
  error: RestApiError | null;
} {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<RestApiError | null>(null);

  const createPreset = useCallback(
    async (input: CreatePresetInput): Promise<Preset> => {
      setLoading(true);
      setError(null);

      try {
        const response = await restClient.post<Preset>(
          "/matching/presets",
          input,
        );
        return response;
      } catch (e) {
        const apiError =
          e instanceof RestApiError
            ? e
            : new RestApiError(
                e instanceof Error ? e.message : "Unknown error",
                500,
              );
        setError(apiError);
        throw apiError;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { createPreset, loading, error };
}

/**
 * プリセットを更新する hook
 * @return {object} updatePreset, loading, error
 */
export function useUpdatePreset(): {
  updatePreset: (presetId: string, input: UpdatePresetInput) => Promise<Preset>;
  loading: boolean;
  error: RestApiError | null;
} {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<RestApiError | null>(null);

  const updatePreset = useCallback(
    async (presetId: string, input: UpdatePresetInput): Promise<Preset> => {
      setLoading(true);
      setError(null);

      try {
        const response = await restClient.put<Preset>(
          `/matching/presets/${presetId}`,
          input,
        );
        return response;
      } catch (e) {
        const apiError =
          e instanceof RestApiError
            ? e
            : new RestApiError(
                e instanceof Error ? e.message : "Unknown error",
                500,
              );
        setError(apiError);
        throw apiError;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { updatePreset, loading, error };
}

/**
 * プリセットを削除する hook
 * @return {object} deletePreset, loading, error
 */
export function useDeletePreset(): {
  deletePreset: (presetId: string) => Promise<void>;
  loading: boolean;
  error: RestApiError | null;
} {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<RestApiError | null>(null);

  const deletePreset = useCallback(async (presetId: string): Promise<void> => {
    setLoading(true);
    setError(null);

    try {
      await restClient.delete(`/matching/presets/${presetId}`);
    } catch (e) {
      const apiError =
        e instanceof RestApiError
          ? e
          : new RestApiError(
              e instanceof Error ? e.message : "Unknown error",
              500,
            );
      setError(apiError);
      throw apiError;
    } finally {
      setLoading(false);
    }
  }, []);

  return { deletePreset, loading, error };
}

/**
 * プリセットの有効/無効を切り替える hook
 * @return {object} toggleActive, loading, error
 */
export function useTogglePresetActive(): {
  toggleActive: (presetId: string) => Promise<Preset>;
  loading: boolean;
  error: RestApiError | null;
} {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<RestApiError | null>(null);

  const toggleActive = useCallback(
    async (presetId: string): Promise<Preset> => {
      setLoading(true);
      setError(null);

      try {
        const response = await restClient.patch<Preset>(
          `/matching/presets/${presetId}/toggle-active`,
        );
        return response;
      } catch (e) {
        const apiError =
          e instanceof RestApiError
            ? e
            : new RestApiError(
                e instanceof Error ? e.message : "Unknown error",
                500,
              );
        setError(apiError);
        throw apiError;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { toggleActive, loading, error };
}

/**
 * フリーテキストをAIで解析する hook
 * @return {object} parseText, loading, error
 */
export function useParsePresetText(): {
  parseText: (text: string, presetType: string) => Promise<ParseTextResult>;
  loading: boolean;
  error: RestApiError | null;
} {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<RestApiError | null>(null);

  const parseText = useCallback(
    async (text: string, presetType: string): Promise<ParseTextResult> => {
      setLoading(true);
      setError(null);

      try {
        const response = await restClient.post<ParseTextResult>(
          "/matching/presets/parse-text",
          { text, presetType },
        );
        return response;
      } catch (e) {
        const apiError =
          e instanceof RestApiError
            ? e
            : new RestApiError(
                e instanceof Error ? e.message : "Unknown error",
                500,
              );
        setError(apiError);
        throw apiError;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { parseText, loading, error };
}

/**
 * プリセット別マッチング結果レスポンスの型
 */
interface PresetResultsResponse {
  items: MatchingResultItem[];
  total: number;
  page: number;
  limit: number;
}

/**
 * プリセットにマッチした結果一覧を取得する hook
 * @param {string | undefined} presetId プリセットID
 * @return {object} results, total, loading, error, refetch
 */
export function usePresetResults(presetId: string | undefined): {
  results: MatchingResultItem[];
  total: number;
  loading: boolean;
  error: RestApiError | null;
  refetch: () => Promise<void>;
} {
  const [results, setResults] = useState<MatchingResultItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(!!presetId);
  const [error, setError] = useState<RestApiError | null>(null);

  const fetchResults = useCallback(async () => {
    if (!presetId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await restClient.get<PresetResultsResponse>(
        `/matching/presets/${presetId}/results`,
      );
      setResults(response.items);
      setTotal(response.total);
    } catch (e) {
      if (e instanceof RestApiError) {
        setError(e);
      } else {
        setError(
          new RestApiError(
            e instanceof Error ? e.message : "Unknown error",
            500,
          ),
        );
      }
    } finally {
      setLoading(false);
    }
  }, [presetId]);

  useEffect(() => {
    fetchResults();
  }, [fetchResults]);

  return { results, total, loading, error, refetch: fetchResults };
}
